import { useCallback, useEffect, useState } from 'react'
import {
  Activity,
  CalendarCheck,
  Eye,
  Globe2,
  Monitor,
  Smartphone,
  Tablet,
  Users,
} from 'lucide-react'
import {
  fetchStats,
  localStats,
  formatFaDateTime,
  formatFaRelative,
  ONLINE_WINDOW_MS,
  type StatsSnapshot,
  type DeviceInfo,
} from '../lib/analytics'

const REFRESH_INTERVAL_MS = 10_000

const deviceIcon = (deviceType: DeviceInfo['deviceType']) => {
  if (deviceType === 'موبایل') return Smartphone
  if (deviceType === 'تبلت') return Tablet
  return Monitor
}

/** نقطهٔ کوچک آنلاین/آفلاین */
const PulseDot = ({ online }: { online: boolean }) => (
  <span className="relative flex h-2.5 w-2.5 shrink-0">
    {online && (
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-60" />
    )}
    <span
      className={`relative inline-flex h-2.5 w-2.5 rounded-full ${online ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-700'}`}
    />
  </span>
)

/** کارت آمار بزرگ — بالای داشبورد */
const StatCard = ({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: typeof Users
  label: string
  value: number
  hint: string
  tone: 'primary' | 'secondary' | 'neutral'
}) => (
  <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-white/[0.03] p-6 md:p-8 shadow-card dark:shadow-none">
    <div className="flex items-start justify-between mb-4 md:mb-6">
      <span
        className={`h-12 w-12 md:h-14 md:w-14 rounded-2xl flex items-center justify-center ${
          tone === 'primary'
            ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300'
            : tone === 'secondary'
              ? 'bg-secondary-100 dark:bg-secondary-900/40 text-secondary-600 dark:text-secondary-300'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
        }`}
      >
        <Icon className="h-5 w-5 md:h-6 md:w-6" />
      </span>
    </div>
    <p className="font-display text-5xl md:text-6xl text-gray-900 leading-none mb-2">{value.toLocaleString('fa-IR')}</p>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-xs text-gray-400 mt-1">{hint}</p>
  </div>
)

const AdminPage = () => {
  const [stats, setStats] = useState<StatsSnapshot | null>(null)
  const [lastUpdated, setLastUpdated] = useState<number>(0)

  const refresh = useCallback(() => {
    fetchStats().then((serverStats) => {
      const next = serverStats ?? localStats()
      setStats(next)
      setLastUpdated(Date.now())
    })
  }, [])

  useEffect(() => {
    refresh()
    const interval = window.setInterval(refresh, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [refresh])

  const onlineNow = stats?.onlineNow ?? 0
  const uniqueVisitors = stats?.uniqueVisitors ?? 0
  const totalVisits = stats?.totalVisits ?? 0
  const submissions = stats?.submissions ?? []
  const visits = stats?.visits ?? []

  return (
    <div className="min-h-screen bg-[#faf7f2] dark:bg-[#17120f] text-gray-800 dark:text-gray-100">
      {/* نوار بالای صفحه — فقط عنوان، هیچ دکمه‌ای وجود ندارد */}
      <header className="sticky top-0 z-10 bg-[#faf7f2]/85 dark:bg-[#17120f]/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="container-custom h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-primary-500 text-white flex items-center justify-center">
              <Activity className="h-4 w-4" />
            </span>
            <div className="leading-tight">
              <p className="text-lg font-bold">الونا · داشبورد مدیریت</p>
              <p className="text-[11px] text-gray-500">
                {stats?.source === 'server' ? 'اتصال به سرور آمار' : 'حالت آفلاین — داده‌های همین مرورگر'}
                {lastUpdated > 0 && ` · آخرین به‌روزرسانی ${formatFaRelative(lastUpdated)}`}
              </p>
            </div>
          </div>
          {/* چرخش خودکار؛ آیکن فقط نشان‌گر وضعیت است — دکمه نیست */}
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary-500" />
            </span>
            زنده
          </span>
        </div>
      </header>

      <main className="container-custom py-10 md:py-14">
        {/* کارت‌های آمار */}
        <section className="grid gap-5 md:gap-6 sm:grid-cols-2 xl:grid-cols-4 mb-10 md:mb-14">
          <StatCard
            icon={Users}
            label="آنلاین در همین لحظه"
            hint={`فعال در ${Math.round(ONLINE_WINDOW_MS / 1000)} ثانیهٔ اخیر`}
            value={onlineNow}
            tone="primary"
          />
          <StatCard
            icon={Eye}
            label="بازدیدکنندهٔ یکتا"
            hint="بر اساس شناسهٔ دستگاه (بدون تکرار)"
            value={uniqueVisitors}
            tone="secondary"
          />
          <StatCard
            icon={Globe2}
            label="مجموع بازدیدها"
            hint="هر باز شدن جدید سایت یک بازدید"
            value={totalVisits}
            tone="neutral"
          />
          <StatCard
            icon={CalendarCheck}
            label="فرم‌های ثبت‌شده"
            hint="درخواست‌های مشاوره"
            value={submissions.length}
            tone="primary"
          />
        </section>

        {/* جدول دستگاه‌ها */}
        <section className="mb-10 md:mb-14">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-secondary-100 dark:bg-secondary-900/40 text-secondary-600 dark:text-secondary-300 flex items-center justify-center">
              <Monitor className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-gray-900">بازدیدکننده‌ها بر اساس دستگاه</h2>
              <p className="text-sm text-gray-500">هر ردیف یک دستگاه یکتاست</p>
            </div>
          </div>

          {visits.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center text-gray-500">
              هنوز بازدیدی ثبت نشده است.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-white/[0.03] shadow-card dark:shadow-none">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 text-gray-500">
                    <th className="text-right font-medium px-5 py-4">وضعیت</th>
                    <th className="text-right font-medium px-5 py-4">دستگاه</th>
                    <th className="text-right font-medium px-5 py-4 hidden md:table-cell">نوع</th>
                    <th className="text-right font-medium px-5 py-4 hidden lg:table-cell">نمایشگر</th>
                    <th className="text-right font-medium px-5 py-4">بازدیدها</th>
                    <th className="text-right font-medium px-5 py-4 hidden md:table-cell">اولین بازدید</th>
                    <th className="text-right font-medium px-5 py-4">آخرین فعالیت</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.slice(0, 50).map((visit) => {
                    const isOnline = Date.now() - visit.lastSeen < ONLINE_WINDOW_MS
                    const DeviceIcon = deviceIcon(visit.device?.deviceType ?? 'دسکتاپ')
                    return (
                      <tr
                        key={visit.clientId}
                        className="border-b border-gray-100 dark:border-gray-800/60 last:border-0"
                      >
                        <td className="px-5 py-4">
                          <span className="flex items-center gap-2">
                            <PulseDot online={isOnline} />
                            <span className={isOnline ? 'text-primary-600 dark:text-primary-300' : 'text-gray-400'}>
                              {isOnline ? 'آنلاین' : 'آفلاین'}
                            </span>
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="flex items-center gap-3">
                            <span className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 flex items-center justify-center shrink-0">
                              <DeviceIcon className="h-4 w-4" />
                            </span>
                            <span className="whitespace-nowrap">{visit.device?.label ?? 'ناشناس'}</span>
                          </span>
                        </td>
                        <td className="px-5 py-4 hidden md:table-cell text-gray-500">
                          {visit.device?.deviceType ?? '—'}
                        </td>
                        <td className="px-5 py-4 hidden lg:table-cell text-gray-500" dir="ltr">
                          {visit.device?.screen ?? '—'}
                        </td>
                        <td className="px-5 py-4 font-medium">{visit.visits.toLocaleString('fa-IR')}</td>
                        <td className="px-5 py-4 hidden md:table-cell text-gray-500">
                          {visit.firstSeen ? formatFaDateTime(visit.firstSeen) : '—'}
                        </td>
                        <td className="px-5 py-4 text-gray-500">{formatFaRelative(visit.lastSeen)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* فرم‌های مشاوره */}
        <section className="mb-16">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 flex items-center justify-center">
              <CalendarCheck className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-gray-900">درخواست‌های مشاوره</h2>
              <p className="text-sm text-gray-500">اطلاعات افرادی که فرم را پر کرده‌اند</p>
            </div>
          </div>

          {submissions.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center text-gray-500">
              هنوز فرمی ثبت نشده است.
            </p>
          ) : (
            <div className="grid gap-4 md:gap-5 md:grid-cols-2 xl:grid-cols-3">
              {submissions.slice(0, 60).map((entry) => {
                const DeviceIcon = deviceIcon(entry.device?.deviceType ?? 'دسکتاپ')
                return (
                  <article
                    key={entry.id}
                    className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-white/[0.03] p-6 shadow-card dark:shadow-none"
                  >
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <p className="font-display text-xl md:text-2xl text-gray-900">{entry.name}</p>
                        <p className="text-sm text-gray-500" dir="ltr" style={{ textAlign: 'right' }}>
                          {entry.phone}
                        </p>
                      </div>
                      <span className="h-9 w-9 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 flex items-center justify-center shrink-0">
                        <DeviceIcon className="h-4 w-4" />
                      </span>
                    </div>

                    {entry.message && (
                      <p className="text-sm text-gray-600 leading-7 mb-4 border-r-2 border-secondary-300 pr-3">
                        {entry.message}
                      </p>
                    )}

                    <div className="text-xs text-gray-400 space-y-1 border-t border-gray-100 dark:border-gray-800 pt-3">
                      <p>ثبت: {formatFaDateTime(entry.createdAt)}</p>
                      <p>دستگاه: {entry.device?.label ?? 'ناشناس'}</p>
                      <p>نمایشگر: {entry.device?.screen ?? '—'}</p>
                    </div>
                  </article>
                )
              })}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-gray-200 dark:border-gray-800 py-6 text-center text-xs text-gray-400">
        الونا · این صفحه بخشی از سایت عمومی نیست و هیچ دکمه‌ای ندارد.
      </footer>
    </div>
  )
}

export default AdminPage
